import { useState } from "react";
import SEO from "../../components/SEO";

export default function LeadIndications() {
  const [bcModal, setBcModal] = useState(false);
  const [ivtModal, setIvtModal] = useState(false);
  const leadIndicationsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Lead Indication",
    "url": "https://www.thrombio.com.au/technology/leadIndication",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    },
    "about": {
      "@type": "MedicalEntity",
      "name": "TBO-309",
      "description": "TBO-309 is a novel antiplatelet compound currently in Phase II clinical trials for the treatment of acute ischemic stroke."
    }
  };

  
  return (
    <>
      <SEO
        title="Lead Indications"
        description="Dive into our clinical trial and discover TBO-309's breakthroughs in therapies redefining patient recovery and care, and learn about our research advancing adjunctive antithrombotic therapies to safely improve thrombolysis and prevent rethrombosis"
        keywords={['Acute Ischaemic Stroke', 'Large Vessel Occlusion', 'Clinical Trials', 'STARS', 'Blood Clots', 'Stroke Treatment']}
        ogImage="/frame100.webp"
        schema={leadIndicationsSchema}
      />

      <BloodClotModal setBcModal={setBcModal} bcModal={bcModal} />
      <IVTModal setIvtModal={setIvtModal} ivtModal={ivtModal} />
      <div
        className="xl:page-width px-12 pt-[190px] md:pt-[250px] text-[2.1rem] leading-[3.1rem] tracking-[.042rem]"
        id="acute-ischaemic-stroke"
      >
        <div className="text-[4.8rem] font-bold leading-[52px] tracking-[0.141rem] [text-shadow:_0px_4px_9px_rgb(0_0_0_/_1.00)]">
          Lead Indications
        </div>
        <div className="flex flex-col gap-[110px] md:gap-10">
          <div className="pt-[90px] max-md:text-center font-syne text-[3.3rem] font-bold leading-[3.2rem] tracking-[.165rem] text-[#E0FFFF]">
            Acute Ischaemic Stroke
          </div>
          <div className="text-[2.1rem] leading-[3.1rem] tracking-[.04rem] max-w-prose">
            ‘Best practice’ treatment of stroke is a complex paradigm, with an evolving standard of care that is
            constantly being driven forward with evidence-based research and clinical trials. The societal cost of
            stroke is large, and the bulk of the economic cost is borne by Federal and State Governments. The annual
            cost of stroke in Australia was estimated to be $6.2 billion in direct financial costs and a further $26.0
            billion in lost wellbeing and premature mortality. <br />
            <br />
            With no new approved pharmacological therapy for stroke in 17 years, the demonstration that our drugs are
            safe and effective would constitute a landmark achievement in the treatment of stroke.
          </div>
        </div>
        <div className="flex md:hidden flex-col gap-[60px] items-center justify-center pt-[83px]">
          <img src="/frame100.webp" alt="" className="pt-[83px]" />
          <img src="/1_AIS.svg" alt="" />
          <div className="flex flex-col gap-[18px]">
            <img src="/Lead_Indications_Stat-5.png" alt="" />
            {/* <img src="/1stat51.webp" alt="" /> */}
          </div>
        </div>
        {/* <div className="py-[85px] max-md:hidden">
          <img src="/framestatistics.webp" alt="" />
        </div> */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-12 mt-28 justify-items-center">
          <img src="/1stat11.webp" alt="" />
          <img src="/1stat21.webp" alt="" />
          <img src="/1stat31.webp" alt="" />
          <img src="/1stat112.webp" alt="" />
          <div className="md:col-span-2 lg:col-span-4 flex flex-col gap-4 items-center justify-center">
            <img src="/Lead_Indications_Stat-5.png" alt="" width="477" />
            {/* <img src="/1stat512.webp" alt="" /> */}
          </div>
        </div>
        <div className="pt-[93px] grid md:grid-cols-2 xl:flex md:items-center gap-[2rem] text-[2rem] leading-[3.2rem] tracking-[.06rem]">
          <div className="flex flex-col gap-8 max-w-prose">
            <div>
            Treatment of stroke requires the rapid removal of occlusive blood clot(s), with the objective to quickly reopen 
            (recanalize) occluded blood vessel(s) and restore blood flow to cerebral tissue (reperfusion), the latter strongly 
            predictive of a favourable outcome.
            </div>
            <div>
              Existing standard-of-care stroke therapies aim to reperfuse ischemic brain tissue with either{" "}
              <span
                onClick={() => setBcModal(true)}
                className="text-[2.1rem] font-bold leading-[3.1rem] tracking-[.042rem] md:text-[2.55rem] md:leading-[3.3rem] md:underline md:tracking-[.01rem] md:uppercase md:text-[#E0FFFF] cursor-pointer"
              >
                Blood clots
              </span>{" "}
              or endovascular thrombectomy (EVT). Intravenous thrombolysis aims to pharmacologically dissolve blood
              clots while thrombectomy mechanically extracts them through surgical intervention. In some patients, a
              combination of both can be used in succession.
            </div>
          </div>
          <img src="/3.2.tleadindictationsgraphic2bloodclotcomponents1.webp" alt="" />
        </div>
        <div className="pt-[78px] flex flex-col gap-8 max-w-[80ch]">
          <div className="font-syne text-[#DA0912] text-[2.4rem] font-bold leading-[3.6rem] tracking-[.12rem]">
            Current standard-of-care therapies
          </div>
          <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem]">
            Treatment of stroke requires the rapid removal of occlusive blood clot(s), with the objective to quickly
            reopen (recanalize) occluded blood vessel(s) and restore blood flow to cerebral tissue (reperfusion), the
            latter strongly predictive of a favourable outcome.
          </div>
        </div>
        <div className="pt-[78px] flex flex-col gap-9 text-[2.1rem] leading-[3.1rem] tracking-[.042rem] max-w-[80ch]">
          <div>
            Despite its approval several decades ago, intravenous thrombolysis remains the only pharmacological approach
            approved by regulatory authorities for the acute treatment of stroke. Thrombolysis is primarily undertaken
            with rtPA, which is associated with numerous limitations including:
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex gap-8 md:gap-20">
              <div>1</div>
              <div>Time-restricted administration, within 4.5 hours of stroke onset.</div>
            </div>
            <div className="flex gap-8 md:gap-20">
              <div>2</div>
              <div>
                Relatively high rate of failed thrombolysis (especially in large arteries - Large Vessel Occlusions or
                LVOs - where recanalisation rates can be 20%).
              </div>
            </div>
            <div className="flex gap-8 md:gap-20">
              <div>3</div>
              <div>High rates of rethrombosis in patients with atherosclerotic disease (up to 50%).</div>
            </div>
            <div className="flex gap-8 md:gap-20">
              <div>4</div>
              <div>6-8% risk of symptomatic intracerebral haemorrhage (ICH).</div>
            </div>
          </div>
          <div className="pt-8 flex flex-col gap-8">
            <div>
              These limitations have led to strict eligibility criteria, wherein rtPA is only administered to 10-13% of
              all stroke patients, and less than half of these achieve a good therapeutic outcome. Of further concern,
              an even worse prognosis faces stroke patients living in remote and regional areas.
            </div>
            <div>
              The recent introduction of thrombectomy has had a major positive impact on stroke outcomes in patients,
              particularly those with Large Vessel Occlusions (LVOs), who have historically had the lowest rates of
              successful reperfusion with thrombolysis alone. Although highly efficacious, thrombectomy is
              time-consuming, expensive, and can only be performed in major tertiary stroke centres, limiting its
              availability to stroke patients in regional and remove communities.
            </div>
          </div>
          <div className="pt-4 text-[#C3EEFD] text-center font-syne text-[2.7rem] leading-[3.3rem] tracking-[.108rem] ">
            rtPA remains the mainstay of stroke treatment globally
          </div>
        </div>
        <div className="pt-[202px] pb-[140px] md:py-[155px] xl:py-[180px] flex justify-center">
          <img src="/2_AIS.svg" alt="" id="large-vessel-occlusion" />
        </div>
        <div className="flex flex-col max-xl:items-center">
          <div className="text-[#E0FFFF] max-md:text-center md:w-full font-syne text-[3.3rem] font-bold leading-[3.2rem] tracking-[.165rem]">
            Large Vessel Occlusion
          </div>
          <div className="pt-[89px] md:pt-[24px] xl:pt-16 text-[2.1rem] leading-[3.1rem] tracking-[.042rem] max-w-[80ch]">
            The size of the brain region affected during stroke is largely dependent on the size of the affected blood
            vessel. Large vessel occlusion strokes (LVOs) occur when there is an interruption of blood flow to one of
            the main arteries in the brain, with one of the most common large vessels being the Middle Cerebral Artery
            (MCA).
          </div>
          <div className="pt-[67px] md:pt-20 w-full xl:flex justify-center">
            <img src="/framegraphic.webp" alt="" className="md:hidden" />
            <img src="/framegraphict.webp" alt="" className="max-md:hidden xl:hidden" />
            <img src="/framegraphicd.webp" alt="" className="max-xl:hidden" />
          </div>
          <div className="pt-24 text-[2.1rem] leading-[3.1rem] tracking-[.042rem] max-w-[80ch]">
            When compared to strokes occurring in the smaller vessels, LVOs have different clinical outcomes and often
            require different treatment strategies. Because large arteries in the brain supply a substantial area of
            cerebral tissue, LVOs can cause serious and potentially permanent neurological impairment, affecting a
            stroke victim’s physical and mental abilities. They also tend to cause swelling in the brain (or oedema),
            which exacerbates brain injury. As a result, recovery is generally slow, and rehabilitation is almost always
            a necessity.
            <br />
            <br />
            LVO stroke in the setting of “tandem” carotid artery disease (CAD, development of
            atherosclerotic plaque in diseased arteries - see FIG) otherwise referred to as Tandem occlusions (TO), are
            present in up to 1-in-3 of all patients with stroke undergoing EVT. TOs are considered one of the most
            challenging stroke patient subpopulations to treat, with high complication rates, and are associated with
            worse clinical outcomes (high rates of disability and death).
          </div>
        </div>
        <div className="flex flex-col gap-8 pt-[94px]">
          <div className="font-syne text-[#DA0912] text-[2.4rem] font-bold leading-[3.6rem] tracking-[.12rem]">
            Current standard-of-care for LVO/TO
          </div>
          <div className="text-[2.1rem] leading-[3.1rem] tracking-[.042rem] flex flex-col gap-8 max-w-[80ch]">
            <div>
            rtPA is inadequate in situations where there is occlusion of large cerebral arteries and there is increasing use of EVT in these patients. 
            Stenting of diseased atherosclerotic arteries is becoming more common, and represents a unique problem for the patient and a hazardous dilemma 
            for the surgeon. Combinations of antithrombotic drugs are increasingly used to maintain stent patency (aspirin combined with clopidogrel or ticagrelor). 
            </div>
            <div>
            Combinations of these anti-platelet therapies in stroke can promote disabling brain haemorrhage (6-fold increased risk of haemorrhage with GPIIb-IIIa 
            inhibitors) and inadequate platelet inhibition can cause acute stent thrombosis, which can worsen patient outcomes. Safer antithrombotic agents for use 
            with stenting for intracranial cerebral atherosclerosis is desperately needed.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function BloodClotModal(props) {
  const { bcModal, setBcModal } = props;
  return (
    <div
      onClick={() => setBcModal(false)}
      className={`fixed size-full inset-0 bg-black/50 flex justify-center items-center transition-all ${!bcModal ? "scale-0 opacity-0" : "opacity-100"}`}
    >
        <div onClick={(e) => e.stopPropagation()} className="rounded-[56px] bg-[#03181F] flex gap-2 relative">
        <div className="absolute right-10 top-10 z-10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="size-10 stroke-2"
            onClick={() => setBcModal(false)}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </div>
        <div className="flex gap-4 flex-col max-md:pr-16 pl-16 py-16 prose">
          <div className="font-syne text-[#E0FFFF] text-[3rem] leading-[3.6rem] tracking-[.15rem]">
            Blood clot formation:
          </div>
          <div className="font-syne text-[#C3EEFD] text-[2.1rem] leading-[2.8rem] tracking-[.1rem]">
            A natural process that is exaggerated in cardiovascular disease
          </div>
          <div className="text-[1.6rem] leading-[2.5rem] tracking-[.06rem] text-white">
            Blood clots form in healthy individuals, in a natural response to tissue injury. Blood clots are made up of
            blood clotting cells (known as platelets), a protein meshwork (fibrin polymers), and red blood cells, and
            are essentially Mother natures' answer to "bricks and mortar" (FIG), plugging sites of blood vessel damage
            that would otherwise allow blood loss. <br />
            Having facilitated wound healing, blood clots are removed through a naturally occurring process known as
            Fibrinolysis, an enzymatic breakdown of fibrin in blood clots. The enzyme Plasmin cuts the fibrin mesh at
            various places, leading to the production of circulating fragments that are cleared by other proteases.
            Primary fibrinolysis is also a normal body process.
          </div>
        </div>
        <img
          src="/frame115.webp"
          alt="Scientific schematic showing an occlusive blood clot in a diseased blood vessel"
          className="max-md:absolute inset-0 size-full object-contain max-md:opacity-15"
        />
      </div>
    </div>
  );
}

function IVTModal(props) {
  const { ivtModal, setIvtModal } = props;
  return (
    <div
      onClick={() => setIvtModal(false)}
      className={`fixed size-full inset-0 bg-black/50 flex justify-center items-center transition-all ${!ivtModal ? "scale-0 opacity-0" : "opacity-100"}`}
    >
      <div onClick={(e) => e.stopPropagation()} className="md:rounded-[56px] bg-[#03181F] flex gap-2 p-16 max-w-[850px] max-md:max-h-[75dvh] ">
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <div className="font-syne text-[#E0FFFF] text-[3rem] leading-[3.6rem] tracking-[.15rem]">
              Blood clot removal:
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-10 stroke-2"
              onClick={() => setIvtModal(false)}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </div>
          <div className="overflow-y-scroll flex flex-col gap-6">
            <div className="flex justify-between gap-10">
              <div className="flex gap-4 flex-col justify-between">
                <div className="text-[1.6rem] leading-[2.5rem] tracking-[.06rem] text-white">
                  Having facilitated wound healing, blood clots are removed through a naturally occurring process known
                  as FIBRINOLYSIS, an enzymatic breakdown of fibrin in blood clots. The enzyme PLASMIN cuts the fibrin
                  mesh at various places, leading to the production of circulating fragments that are cleared by other
                  proteases. Fibrinolysis is a normal (endogenous) process.
                </div>
                <div className="text-[#DA0912] font-syne text-[1.8rem] leading-[2.4rem] tracking-[.07rem]">
                  Stroke therapy [Thrombolysis] adopts and enhances the natural process of fibrinolysis:
                </div>
              </div>
              <img src="/ae279dedb5c71f82cf86c7422027b123.png" alt="A blood clot in the brain (composed of platelets, red blood cells and fibrin) treated with TPA dissolving the fibrin, releasing thrombin" className="max-md:hidden size-[300px]" />
            </div>
            <div className="text-[1.6rem] leading-[2.5rem] tracking-[.06rem] text-white max-md:pb-12">
              The endogenous enzymatic process used to remove clots is mimicked in order to treat stroke. An artificial
              (or recombinant) version of a naturally occurring protein - tissue plasminogen activator (rtPA) - is
              injected into the blood stream of stroke patients, to trigger fibrinolysis, with the ultimate aim to
              remove dangerous blood clots and restore blood flow. This type of therapy - referred to as intravenous
              "Thrombolytic therapy" (IVT) a - is exquisitely time-sensitive - wherein only rapid removal of obstructive
              clots and restoration of blood flow to the brain can prevent permanent brain damage and death.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}